import { useI18n } from '../../../i18n';
// ============================================
// CategoryTable Component
// ============================================

import { Pagination } from '../../../components/shared';
import { EditIcon, TrashIcon } from '../../../components/icons';

export interface CategoryTableItem {
  id: string;
  name: string;
  slug: string;
  parentId: string | null;
  parentName: string;
  /**
   * Bug #66: `null` = payload KHÔNG mang `children_count` (BE cũ) -> hiện "—".
   * Số 0 do BE trả về là dữ liệu THẬT ("không có danh mục con") và phải hiện
   * đúng là 0, không được gộp chung với trạng thái thiếu dữ liệu.
   */
  childrenCount: number | null;
  /**
   * Tên các danh mục con CÓ TRÊN TRANG HIỆN TẠI, đã xếp đúng thứ tự hiển thị.
   * Rỗng không có nghĩa là không có con — con có thể nằm ở trang khác; lúc đó
   * `childrenCount` mới là số thật.
   */
  childrenNames: string[];
  isActive: boolean;
  sortOrder: number;
  level: number;
}

interface CategoryTableProps {
  data: CategoryTableItem[];
  isLoading: boolean;
  onEdit: (item: CategoryTableItem) => void;
  onDelete: (item: CategoryTableItem) => void;
  /**
   * Đổi thứ tự hiển thị (sheet 2 mục #17) — hoán đổi `sort_order` của item với
   * hàng xóm LIỀN KỀ CÙNG CẤP. Không truyền thì cột này ẩn.
   */
  onMove?: (item: CategoryTableItem, direction: 'up' | 'down') => void;
  isReordering?: boolean;
  totalItems: number;
  currentPage: number;
  pageSize: number;
  totalPages: number;
  onPageChange: (page: number) => void;
}

export function CategoryTable({
  data, isLoading,
  onEdit, onDelete, onMove, isReordering = false,
  totalItems, currentPage, pageSize, totalPages, onPageChange,
}: CategoryTableProps) {
  const { t } = useI18n();

  /**
   * Có hàng xóm cùng cấp & cùng cha để hoán đổi hay không. Trang cha xếp bảng
   * theo CÂY (con đứng ngay sau cha), nên giữa hai anh em có thể xen cả cụm con
   * của người phía trên — vòng lặp bước qua chúng và chỉ dừng ở dòng cùng
   * `level` + cùng `parentId`. Nhờ vậy nút của một danh mục con không bao giờ
   * bắt được dòng ngoài phạm vi anh em cùng cha.
   */
  const findSibling = (index: number, direction: 'up' | 'down'): number => {
    const current = data[index];
    const step = direction === 'up' ? -1 : 1;
    for (let i = index + step; i >= 0 && i < data.length; i += step) {
      if (data[i].level === current.level && data[i].parentId === current.parentId) return i;
    }
    return -1;
  };

  return (
    <div className="rounded-xl border border-slate-100 bg-white shadow-sm">
      {/* Table */}
      <div className="overflow-x-auto">
      <table className="w-full min-w-[640px]">
        <thead >
          <tr className="border-b border-slate-100 bg-slate-50/50">
            <th className="px-6 py-3 text-left text-xs font-semibold uppercase tracking-wider text-slate-500">{t('categoriesCategory')}</th>
            <th className="px-6 py-3 text-left text-xs font-semibold uppercase tracking-wider text-slate-500">{t('parent')}</th>
            <th className="px-6 py-3 text-center text-xs font-semibold uppercase tracking-wider text-slate-500">{t('children')}</th>
            <th className="px-6 py-3 text-center text-xs font-semibold uppercase tracking-wider text-slate-500">{t('categoriesStatus')}</th>
            <th className="px-6 py-3 text-center text-xs font-semibold uppercase tracking-wider text-slate-500">{t('categoriesActions')}</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-slate-100 ">
          {isLoading ? (
            <tr>
              <td colSpan={5} className="px-6 py-12 text-center">
                <div className="inline-block h-8 w-8 animate-spin rounded-full border-b-2 border-blue-600" />
              </td>
            </tr>
          ) : data.length === 0 ? (
            <tr>
              <td colSpan={5} className="px-6 py-12 text-center text-sm text-slate-500">{t('noCategoriesFound')}</td>
            </tr>
          ) : (
            data.map((item, index) => (
              <tr key={item.id} className="transition-colors hover:bg-slate-50">
                <td className="px-6 py-4">
                  <span
                    className="font-medium"
                    style={{
                      paddingLeft: `${item.level * 16}px`,
                      color: 'var(--text-black-212121, #212121)',
                      fontFamily: 'Inter, sans-serif',
                      fontSize: '15px',
                      fontStyle: 'normal',
                      lineHeight: '22px'
                    }}
                  >
                    {item.level > 0 && <span className="mr-1 text-slate-300">└</span>}
                    {item.name}
                  </span>
                </td>
                <td className="px-6 py-4 text-sm text-slate-500">{item.parentName}</td>
                {/* Danh mục con: hiện TÊN thay vì con số — số 3 không nói được
                    "3 cái nào", mà đó mới là thứ người dùng cần khi soát cây.
                    Dài quá thì cắt bằng "…" (ellipsis của CSS, cắt đúng theo bề
                    rộng cột thật) và để nguyên danh sách đầy đủ trong tooltip.
                    Con nằm ở trang khác không có tên để hiện, nên phần thiếu
                    hiện dưới dạng "+n" lấy từ `childrenCount` của BE. */}
                <td className="px-6 py-4 text-center text-sm text-slate-500">
                  {item.childrenNames.length > 0 ? (
                    <div className="flex items-center justify-center gap-1.5">
                      <span
                        className="max-w-[240px] truncate text-slate-600"
                        title={item.childrenNames.join(', ')}
                      >
                        {item.childrenNames.join(', ')}
                      </span>
                      {item.childrenCount !== null && item.childrenCount > item.childrenNames.length && (
                        <span
                          className="shrink-0 rounded-full bg-slate-100 px-2 py-0.5 text-xs font-medium text-slate-500"
                          title={t('children')}
                        >
                          +{item.childrenCount - item.childrenNames.length}
                        </span>
                      )}
                    </div>
                  ) : item.childrenCount === null ? (
                    // BE cũ không gửi `children_count` — "chưa biết", khác hẳn 0.
                    <span className="text-slate-300">—</span>
                  ) : item.childrenCount > 0 ? (
                    // Có con thật nhưng cả cụm nằm ở trang khác: giữ con số.
                    <span className="inline-block rounded-full bg-slate-100 px-2.5 py-0.5 text-xs font-medium text-slate-600">{item.childrenCount}</span>
                  ) : (
                    <span className="text-slate-300">0</span>
                  )}
                </td>
                <td className="px-6 py-4 text-center">
                  <span className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium ${item.isActive ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-600'}`}>
                    {item.isActive ? t('activeStatus') : t('inactiveStatus')}
                  </span>
                </td>
                <td className="px-6 py-4">
                  <div className="flex items-center justify-center gap-2">
                    {/* Đổi thứ tự — chỉ bật khi có hàng xóm cùng cấp cùng cha */}
                    {onMove ? (
                      <>
                        <button
                          onClick={() => onMove(item, 'up')}
                          disabled={isReordering || findSibling(index, 'up') === -1}
                          className="rounded p-1 text-slate-400 transition-colors hover:bg-slate-100 hover:text-blue-600 disabled:cursor-not-allowed disabled:opacity-30"
                          title={t('categoryMoveUp')}
                        >
                          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <path d="M18 15l-6-6-6 6" />
                          </svg>
                        </button>
                        <button
                          onClick={() => onMove(item, 'down')}
                          disabled={isReordering || findSibling(index, 'down') === -1}
                          className="rounded p-1 text-slate-400 transition-colors hover:bg-slate-100 hover:text-blue-600 disabled:cursor-not-allowed disabled:opacity-30"
                          title={t('categoryMoveDown')}
                        >
                          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <path d="M6 9l6 6 6-6" />
                          </svg>
                        </button>
                      </>
                    ) : null}
                    <button onClick={() => onEdit(item)} className="rounded p-1 text-slate-400 transition-colors hover:bg-slate-100 hover:text-blue-600" title={t('categoriesEdit')}>
                      <EditIcon size={16} />
                    </button>
                    <button onClick={() => onDelete(item)} className="rounded p-1 text-slate-400 transition-colors hover:bg-red-50 hover:text-red-500" title={t('categoriesDelete')}>
                      <TrashIcon size={16} />
                    </button>
                  </div>
                </td>
              </tr>
            ))
          )}
        </tbody>
      </table>
      </div>

      {/* Pagination footer */}
      {!isLoading && totalItems > 0 && (
        <div className="border-t border-slate-100">
          <Pagination
            variant="numbered"
            currentPage={currentPage}
            totalPages={totalPages}
            totalItems={totalItems}
            pageSize={pageSize}
            onPageChange={onPageChange}
            itemLabel="categories"
          />
        </div>
      )}
    </div>
  );
}
